import React from 'react'
import { styled } from '@mui/material/styles'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Link from '@mui/material/Link'

import CloseIcon from '@mui/icons-material/Close'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { Logger } from 'wdc-cube'

import {
    bannerSx,
    buttonSx,
    chipSx,
    classToFComponent,
    FCClassContext,
    KeyRow,
    KeyValues,
    LifesHubColors,
    LifesHubEmptyState,
    LifesHubRadius,
    LocaleProvider,
    ModalClose,
    ModalFooter,
    ModalFooterActions,
    ModalHead,
    WorkingBar
} from 'src/utils/views'
import * as fmtUtils from 'src/utils/formatter-utils'

import { type ProfessionalDetailScope } from '../ta_scopes'
import { type MedicoBean, type VinculoBean } from '../ta_service'

const LOG = Logger.get('ProfessionalDetailDialog')

/** Códigos de dbo: 1 nula, 2 ativa, 3 suspensa, 4 inapta, 8 baixada. */
const SITUACAO_ACCENT: Record<number, string> = {
    1: 'var(--border)',
    2: LifesHubColors.green700,
    3: LifesHubColors.warning,
    4: LifesHubColors.warning,
    8: 'var(--border)'
}

const Head = styled(ModalHead)({
    padding: '22px 26px 14px',
    marginBottom: 0
})

const Chips = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8
})

const Body = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.05fr)',
    gap: 22,
    '@media (max-width: 900px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
})

const Column = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minWidth: 0
})

const SectionLabel = styled('div')({
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '.09em',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--muted)'
})

const SectionText = styled('div')({
    fontSize: 14,
    color: 'var(--ink)'
})

const SectionHint = styled('div')({
    fontSize: 12,
    color: 'var(--muted)'
})

const VinculoList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxHeight: 360,
    overflowY: 'auto',
    paddingRight: 4
})

const VinculoBox = styled('div')({
    padding: '11px 13px',
    border: '1px solid var(--border)',
    borderLeftWidth: 3,
    borderRadius: LifesHubRadius.panel,
    backgroundColor: '#fbfcfb',
    minWidth: 0
})

const VinculoName = styled('div')({
    fontWeight: 750,
    fontSize: 14,
    lineHeight: 1.3,
    color: 'var(--ink)'
})

const VinculoCodes = styled('div')({
    marginTop: 2,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12,
    color: 'var(--muted)'
})

const VinculoText = styled('div')({
    marginTop: 4,
    fontSize: 13,
    color: 'var(--ink)'
})

const VinculoAddress = styled('div')({
    marginTop: 2,
    fontSize: 13,
    color: 'var(--muted)'
})

const MapLink = styled(Link)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 7,
    fontSize: 13,
    fontWeight: 650,
    color: 'var(--green-800)',
    textDecorationColor: 'rgba(8, 114, 15, .35)',
    '& .MuiSvgIcon-root': { fontSize: 16 }
})

export type ProfessionalDetailProps = {
    scope: ProfessionalDetailScope
}

function mapsUrl(v: VinculoBean) {
    if (v.location) {
        return `https://www.google.com/maps/search/?api=1&query=${v.location.lat},${v.location.lon}`
    }

    if (v.endereco) {
        const busca = v.cep ? `${v.endereco}, ${v.cep}` : v.endereco
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(busca)}`
    }

    return undefined
}

function VinculoCard({ v }: { v: VinculoBean }) {
    const codes: string[] = []
    v.cnpj && codes.push(fmtUtils.formatCNPJ(v.cnpj))
    v.cnes && codes.push(`CNES ${v.cnes}`)
    v.situacaoCadastralDsc && codes.push(v.situacaoCadastralDsc)

    const mapa = mapsUrl(v)
    const accent = SITUACAO_ACCENT[v.situacaoCadastral ?? -1] ?? 'var(--border)'

    return (
        <VinculoBox style={{ borderLeftColor: accent }}>
            <VinculoName>{v.nome}</VinculoName>
            {codes.length > 0 && <VinculoCodes>{codes.join(' · ')}</VinculoCodes>}
            {v.cnae?.descricao && <VinculoText>{v.cnae.descricao}</VinculoText>}
            {v.endereco && <VinculoAddress>{v.endereco}</VinculoAddress>}
            {mapa && (
                <MapLink href={mapa} target="_blank" rel="noopener noreferrer">
                    <PlaceOutlinedIcon />
                    Abrir no mapa
                </MapLink>
            )}
        </VinculoBox>
    )
}

class ProfessionalDetailViewClass implements FCClassContext<ProfessionalDetailProps> {
    scope!: ProfessionalDetailScope

    readonly emitClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitFilter = () => {
        this.scope.onFilterByProfessional().catch(LOG.caught)
    }

    onSyncState({ scope }: ProfessionalDetailProps) {
        this.scope = scope
    }

    render({ scope }: ProfessionalDetailProps) {
        const fns = LocaleProvider.get().fns
        const p = scope.profissional

        return (
            <Dialog
                open={scope.open}
                onClose={this.emitClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-2)',
                            backgroundImage: 'none',
                            color: 'var(--ink)'
                        }
                    }
                }}
            >
                <Head>
                    <div>
                        <h2>{p?.nome ?? 'Ficha do profissional'}</h2>
                        {p && (
                            <Chips>
                                {p.crm?.dscSituacao && (
                                    <Chip
                                        size="small"
                                        label={p.crm.dscSituacao}
                                        sx={chipSx(p.crm.codSituacao === 'A' ? 'green' : 'gray')}
                                    />
                                )}
                                {p.crm?.codigo && (
                                    <Chip
                                        size="small"
                                        label={`CRM ${fmtUtils.formatCRM(p.crm.codigo)}${
                                            p.crm.inscricao ? ` · ${p.crm.inscricao}` : ''
                                        }`}
                                        sx={chipSx('gray')}
                                    />
                                )}
                                {(p.especialidades ?? '')
                                    .split(', ')
                                    .filter(Boolean)
                                    .map((esp) => (
                                        <Chip key={esp} size="small" label={esp} sx={chipSx('blue')} />
                                    ))}
                            </Chips>
                        )}
                    </div>
                    <ModalClose aria-label="Fechar ficha" onClick={this.emitClose}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </ModalClose>
                </Head>

                {scope.loading && <WorkingBar />}

                <DialogContent
                    dividers
                    sx={{ padding: '20px 26px', borderColor: 'var(--border)', backgroundColor: 'var(--surface)' }}
                >
                    {scope.error && (
                        <Alert
                            variant="outlined"
                            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                            sx={bannerSx('danger')}
                        >
                            {scope.error}
                        </Alert>
                    )}

                    {!scope.error && p && (
                        <Body>
                            <Column>
                                <div>
                                    <SectionLabel>Formação</SectionLabel>
                                    {p.instituicaoFormacao ? (
                                        <>
                                            <SectionText>{p.instituicaoFormacao}</SectionText>
                                            {p.anoFormatura && <SectionHint>{p.anoFormatura}</SectionHint>}
                                        </>
                                    ) : (
                                        <SectionHint>Não informada</SectionHint>
                                    )}
                                </div>

                                <div>
                                    <SectionLabel>Registro</SectionLabel>
                                    <KeyValues>
                                        <KeyRow>
                                            <span className="key">Inscrição</span>
                                            <span className="value">
                                                {fns.formatDate(toDate(p.crm?.dataInscricao)) || '—'}
                                            </span>
                                        </KeyRow>
                                        <KeyRow>
                                            <span className="key">1ª na UF</span>
                                            <span className="value">
                                                {fns.formatDate(toDate(p.crm?.dataPrimeiraInscricao)) || '—'}
                                            </span>
                                        </KeyRow>
                                        <KeyRow>
                                            <span className="key">Outros CRM</span>
                                            <span className="value">{p.outrosCRMs || '—'}</span>
                                        </KeyRow>
                                        {p.socio?.qualificacao && (
                                            <KeyRow>
                                                <span className="key">Qualif. sócio</span>
                                                <span className="value">{p.socio.qualificacao}</span>
                                            </KeyRow>
                                        )}
                                    </KeyValues>
                                </div>

                                <div>
                                    <SectionLabel>Contato</SectionLabel>
                                    {hasContato(p) ? (
                                        <KeyValues>
                                            <KeyRow>
                                                <span className="key">Telefone</span>
                                                <span className="value">
                                                    {p.telefones ? fmtUtils.formatTelefone(p.telefones) : '—'}
                                                </span>
                                            </KeyRow>
                                            <KeyRow>
                                                <span className="key">Celular</span>
                                                <span className="value">
                                                    {p.celular ? fmtUtils.formatTelefone(p.celular) : '—'}
                                                </span>
                                            </KeyRow>
                                            <KeyRow>
                                                <span className="key">E-mail</span>
                                                <span className="value">{p.email || '—'}</span>
                                            </KeyRow>
                                            <KeyRow>
                                                <span className="key">Endereço</span>
                                                <span className="value">{p.endereco?.text || '—'}</span>
                                            </KeyRow>
                                        </KeyValues>
                                    ) : (
                                        <SectionHint>Sem dados de contato liberados para o seu perfil.</SectionHint>
                                    )}
                                </div>
                            </Column>

                            <Column>
                                <div>
                                    <SectionLabel>
                                        {scope.vinculos.length > 0 ? `Vínculos · ${scope.vinculos.length}` : 'Vínculos'}
                                    </SectionLabel>

                                    {scope.vinculos.length > 0 ? (
                                        <VinculoList>
                                            {scope.vinculos.map((v) => (
                                                <VinculoCard key={v.id} v={v} />
                                            ))}
                                        </VinculoList>
                                    ) : (
                                        <LifesHubEmptyState
                                            title={
                                                scope.semVinculo
                                                    ? 'Nenhum vínculo identificado'
                                                    : 'Sem vínculos para exibir'
                                            }
                                            text={
                                                scope.semVinculo
                                                    ? 'Este profissional não tem estabelecimento conhecido nas fontes consultadas.'
                                                    : 'Nenhum estabelecimento retornado para este profissional.'
                                            }
                                        />
                                    )}
                                </div>
                            </Column>
                        </Body>
                    )}

                    {!scope.error && !p && !scope.loading && (
                        <LifesHubEmptyState
                            title="Nada para exibir"
                            text="Não há dados carregados para este profissional."
                        />
                    )}
                </DialogContent>

                <ModalFooter>
                    <ModalFooterActions>
                        <Button sx={buttonSx()} onClick={this.emitClose}>
                            Fechar
                        </Button>
                        <Button sx={buttonSx('primary')} onClick={this.emitFilter} disabled={!p}>
                            Filtrar por este profissional
                        </Button>
                    </ModalFooterActions>
                </ModalFooter>
            </Dialog>
        )
    }
}

function toDate(v: string | undefined) {
    if (!v) return undefined
    const d = new Date(v)
    return isNaN(d.getTime()) ? undefined : d
}

function hasContato(p: MedicoBean) {
    return !!(p.telefones || p.celular || p.email || p.endereco?.text)
}

export const ProfessionalDetailView = classToFComponent(ProfessionalDetailViewClass, React)
export default ProfessionalDetailView
